/**
 * This module provides functionality to make the `console` object
 * unavailable to all running Javascript. This is useful when
 * we want to suppress third-party log messages.
 *
 * It also provides a bypass for our own code to log to the console
 * even while others can't.
 *
 * Usage:
 *      ```
 *      replaceConsole();
 *      // third-party operation with lots of console logs runs here
 *      debug.log("Logging from our own code...");
 *      // third-party operation with lots of console logs runs here
 *      restoreConsole();
 *      ```
 *
 *      Note: if using with promises/async functions, timing can be
 *      tricky. For example, make sure you're putting
 *      `restoreConsole()` calls in both `.then()` and `.catch()` or
 *      `catch() {}` blocks -- otherwise, if something unexpected
 *      happens, the console may be disabled for the rest of
 *      the session.
 */

// Save the original console, so we can both call into it,
// and restore it later.
const _original = (window as any).console;

// The "replacement" console that will be swapped in & out.
const _stub = {};

// The "bypass" console that will be available to our code only.
const _debug = {};

// Build the replacement and bypass consoles.
for (const method in (window as any).console) {
    // We don't want the replacement console methods to do anything.
    // In the future, we could aggregate these messages and provide
    // a method to display them later if desired.
    _stub[method] = () => {
    // no-op
    };

    // The bypass console will just pass through all calls
    // to the original.
    _debug[method] = (...args) => {
        _original[method].apply(_original, args);
    };
}

/**
 * A replacement console that will always be available to our code only.
 */
export const debug = _debug as Console;

/**
 * Replace all `console` methods with an empty/no-op function.
 * This affects any Javascript running anywhere on the page, including
 * our legacy code.
 */
export const replaceConsole = () => {
    (window as any).console = _stub;
    _original.log("replaced console");
};

/**
 * Restore the `console` methods to their original versions.
 * This affects any Javascript running anywhere on the page, including
 * our legacy code.
 */
export const restoreConsole = () => {
    (window as any).console = _original;
    _original.log("restored console");
};
